/**
 * Chat Pattern Parser
 * Parses and generates chat messages from simple pattern strings
 * 
 * Pattern Syntax:
 * - [X,O] = choice between X or O
 * - [1-9] = range from 1 to 9
 * - [X,O][1-9] = combination (X1, X2, O3, etc.)
 * - [X,O] - [1-9] = combination with literal text (X - 7, O - 3, etc.)
 * - "generic" = no specific pattern
 */

interface ParsedPattern {
  type: 'choice' | 'range' | 'literal';
  values: string[];
  isLiteral?: boolean; // True for literal text between brackets
}

/**
 * Parse a pattern string into structured format, preserving literals
 * @param pattern - Pattern string like "[X,O] - [1-9]"
 * @returns Array of parsed pattern parts including literals
 */
export function parsePattern(pattern: string): ParsedPattern[] {
  if (!pattern || pattern === 'generic') {
    return [];
  }

  const parts: ParsedPattern[] = [];
  let lastIndex = 0;
  const regex = /\[([^\]]+)\]/g;
  let match;

  while ((match = regex.exec(pattern)) !== null) {
    // Add any literal text before this bracket
    if (match.index > lastIndex) {
      const literalText = pattern.substring(lastIndex, match.index);
      if (literalText) {
        parts.push({ type: 'literal', values: [literalText], isLiteral: true });
      }
    }

    const content = match[1];

    // Check if it's a range (e.g., "1-9", "a-z")
    const rangeMatch = content.match(/^(\w)-(\w)$/);
    if (rangeMatch) {
      const start = rangeMatch[1];
      const end = rangeMatch[2];
      
      // Generate range values
      const values: string[] = [];
      
      // Check if numeric range
      if (/^\d$/.test(start) && /^\d$/.test(end)) {
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        for (let i = startNum; i <= endNum; i++) {
          values.push(i.toString());
        }
      } 
      // Check if alphabetic range
      else if (/^[a-zA-Z]$/.test(start) && /^[a-zA-Z]$/.test(end)) {
        const startCode = start.charCodeAt(0);
        const endCode = end.charCodeAt(0);
        for (let i = startCode; i <= endCode; i++) {
          values.push(String.fromCharCode(i));
        }
      }
      
      if (values.length > 0) {
        parts.push({ type: 'range', values });
        lastIndex = match.index + match[0].length;
        continue;
      }
    }

    // Check if it's a choice (e.g., "X,O,Z")
    if (content.includes(',')) {
      const choices = content.split(',').map(c => c.trim()).filter(c => c.length > 0);
      if (choices.length > 0) {
        parts.push({ type: 'choice', values: choices });
        lastIndex = match.index + match[0].length;
        continue;
      }
    }

    // Otherwise, treat as literal within brackets
    parts.push({ type: 'literal', values: [content] });
    lastIndex = match.index + match[0].length;
  }

  // Add any remaining literal text after the last bracket
  if (lastIndex < pattern.length) {
    const literalText = pattern.substring(lastIndex);
    if (literalText) {
      parts.push({ type: 'literal', values: [literalText], isLiteral: true });
    }
  }

  return parts;
}

/**
 * Generate a random valid chat message from a pattern
 * @param pattern - Pattern string like "[X,O] - [1-9]"
 * @returns Generated chat message
 */
export function generateFromPattern(pattern: string): string {
  if (!pattern || pattern === 'generic') {
    return '';
  }

  const parts = parsePattern(pattern);
  
  if (parts.length === 0) {
    return '';
  }

  // Generate value from each part, preserving literals
  const result = parts.map(part => {
    if (part.isLiteral) {
      // For literal text, just return it as-is
      return part.values[0];
    } else {
      // For choices/ranges, pick random value
      const randomIndex = Math.floor(Math.random() * part.values.length);
      return part.values[randomIndex];
    }
  }).join('');

  return result;
}

/**
 * Validate a pattern string
 * @param pattern - Pattern string to validate
 * @returns True if pattern is valid
 */
export function isValidPattern(pattern: string): boolean {
  if (!pattern) {
    return false;
  }

  // "generic" is always valid
  if (pattern === 'generic') {
    return true;
  }

  // Check if pattern has balanced brackets
  const openCount = (pattern.match(/\[/g) || []).length;
  const closeCount = (pattern.match(/\]/g) || []).length;
  
  if (openCount !== closeCount || openCount === 0) {
    return false;
  }

  // Try to parse - if it succeeds, it's valid
  try {
    const parts = parsePattern(pattern);
    return parts.length > 0 && parts.every(part => part.values.length > 0);
  } catch {
    return false;
  }
}

